import { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useNotify, useRedirect } from 'react-admin';
import { SectionCard } from '../../shared/Panel';
import { PreOrderForm } from '../../pre-order/PreOrderForm';
import type { PreOrderInput } from '../../pre-order/preOrderApi';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { FEATURE_INQUIRIES, createInquiry, toErrorMessage } from './inquiriesApi';

/**
 * Nowe zapytanie klienta (26.10.2026, P3; `clientInquiries`, S-26.10-08).
 * Ten sam formularz co prezlecenie — bo to ten sam rekord; różnica jest
 * w rodzaju: zapytanie nie trafia do banku ani do kolejek, dopóki klient
 * nie przyjmie oferty. Kraj i kod pocztowy wyznaczają punkt do dolotu.
 */
export const InquiryCreate = () => {
  const on = useFeatureState(FEATURE_INQUIRIES);
  const notify = useNotify();
  const redirect = useRedirect();
  const [input, setInput] = useState<PreOrderInput>({});
  const [sourceNote, setSourceNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  const save = () =>
    void (async () => {
      setBusy(true);
      try {
        const created = await createInquiry({ ...input, sourceNote: sourceNote || null });
        notify(`Zapytanie ${created.ref} zapisane`, { type: 'success' });
        redirect('show', 'inquiries', created.id);
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Box sx={{ p: 2, maxWidth: 1100 }} data-testid="inquiry-create-form">
      <SectionCard title="Nowe zapytanie klienta">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Wpisz to, o co pyta klient: miejsca (kraj i kod pocztowy — z nich liczymy dolot), terminy, typ auta, wagę.
          Czego nie wiadomo, zostaw puste. Prezlecenie powstanie z tego samego rekordu dopiero po przyjęciu oferty.
        </Typography>
        <PreOrderForm
          values={input}
          locked={busy}
          onChange={(field, v) => setInput((d) => ({ ...d, [field]: v }))}
          onContractorChange={(c) => setInput((d) => ({ ...d, contractor: c, contractorId: c?.id ?? null }))}
        />
        <TextField
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          label="Skąd zapytanie"
          placeholder="np. telefon od dyspozytora, mail z listą ładunków"
          value={sourceNote}
          disabled={busy}
          onChange={(e) => setSourceNote(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'inquiry-source-note' } }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={busy} onClick={save} data-testid="inquiry-save">
            Zapisz zapytanie
          </Button>
        </Stack>
      </SectionCard>
    </Box>
  );
};
